import type { AxiosInstance, AxiosResponse } from "axios";
import type { CrossTabRefresh, SharedTokenPair } from "../../../AuthStore/crossTabRefresh";
import type { ClientError } from "../../Error/ClientError";
import type { NetworkError } from "../../Error/NetworkError";
import type { ServerError } from "../../Error/ServerError";
import * as Sentry from "@sentry/vue";
import { isAxiosError } from "axios";
import { useCrossTabRefresh } from "../../../AuthStore/crossTabRefresh";
import { holdWhileTokenGrant } from "../../../AuthStore/devHmrReloadHold";

export interface AuthPluginConfigInterface<Output> {
  path: AuthPath
  axios: AxiosInstance
  /**
   * Stores the new token pair, synchronously, before anything waits on it. A caller that only needs the tokens
   * (a request retried after a 401) continues from here, while `onAuthenticateSuccess` may still be loading the
   * session with requests of its own.
   */
  storeTokens?: (response: Output) => void
  onAuthenticateSuccess?: (response: Output) => Promise<void>
  onAuthenticateFailed?: (error: ClientError | ServerError | NetworkError) => Promise<void>
  /** Coordinates refresh token grants across tabs; defaults to this tab's shared coordinator. */
  crossTabRefresh?: () => CrossTabRefresh<SharedTokenPair>
}

export enum AuthPath {
  OAUTH_REFRESH_V2 = "/oauth/v2/token",
}

/**
 * What a caller of `authenticate` waits for. `session` (default): the new tokens and `onAuthenticateSuccess`
 * (the session load). `tokens`: only the new tokens. A request retried after a 401 must wait for `tokens`: the
 * session load of that same refresh may join the very request that is waiting (one query, one key), or get a 401
 * of its own, and waiting for the session there would wait on itself forever.
 */
export type AuthenticateWaitFor = "session" | "tokens";

export interface AuthenticateOptions {
  waitFor?: AuthenticateWaitFor
}

/**
 * A session load that failed after the new tokens were stored, while only callers waiting for the tokens were
 * around: nobody else gets to see this error, so it is reported here. A 5xx is left out, Sentry's HTTP client
 * integration reports those already.
 */
function reportUnawaitedSessionFailure(error: unknown): void {
  const status = isAxiosError(error) ? error.response?.status : undefined;
  if (status !== undefined && status >= 500) {
    return;
  }
  Sentry.captureException(error, { tags: { "auth.refresh_step": "session_load" } });
}

export function createAuthenticatePlugin<Input, Output>(
  config: AuthPluginConfigInterface<Output>,
) {
  // The grant in flight: `tokens` settles once the new pair is stored, `settled` once the session callbacks ran too.
  let inFlight: InFlight | null = null;

  // Start listening for rotations by other tabs right away: a tab that never refreshes itself must still take over
  // the pair when another tab rotates the refresh token they share.
  const crossTabRefresh = config.crossTabRefresh ?? useCrossTabRefresh;
  crossTabRefresh();

  /** The refresh token of a refresh grant; other grants (password, authorization code) are not shared across tabs. */
  function refreshTokenOf(input: Input): string | null {
    if (!input || typeof input !== "object") {
      return null;
    }
    const candidate = input as Record<string, unknown>;
    if (candidate.grant_type !== "refresh_token" || typeof candidate.refresh_token !== "string" || candidate.refresh_token === "") {
      return null;
    }
    return candidate.refresh_token;
  }

  async function exchange(input: Input): Promise<Output> {
    const response: AxiosResponse<Output> = await config.axios.post<Output>(config.path, input);
    return response.data;
  }

  async function grant(input: Input): Promise<Output> {
    const refreshToken = refreshTokenOf(input);
    if (refreshToken === null) {
      return exchange(input);
    }
    const coordinator = crossTabRefresh();
    // A refresh grant answers with a token pair, so the output is the shared pair type.
    return await coordinator.refresh(refreshToken, async () => await exchange(input) as SharedTokenPair) as Output;
  }

  interface InFlight {
    tokens: Promise<Output>
    settled: Promise<Output>
    /** Whether a caller waits for the session; that caller then gets its failure. */
    sessionAwaited: boolean
  }

  function start(input: Input): InFlight {
    let tokensStored = false;
    const tokens = holdWhileTokenGrant(async () => {
      const data = await grant(input);
      config.storeTokens?.(data);
      tokensStored = true;
      return data;
    });

    const settled = tokens
      .then(async (data: Output) => {
        if (config.onAuthenticateSuccess) {
          await config.onAuthenticateSuccess(data);
        }

        return data;
      })
      .catch(async (e: ClientError | ServerError | NetworkError) => {
        if (config.onAuthenticateFailed) {
          await config.onAuthenticateFailed(e);
        }

        throw e;
      })
      .finally(() => {
        inFlight = null;
      });

    const flight: InFlight = { tokens, settled, sessionAwaited: false };

    // Callers that only wait for the tokens never await `settled`, so it must not surface as an unhandled
    // rejection. A failed grant already went to `onAuthenticateFailed`; a failed session load is reported once.
    settled.catch((error: unknown) => {
      if (tokensStored && !flight.sessionAwaited) {
        reportUnawaitedSessionFailure(error);
      }
    });

    return flight;
  }

  async function authenticate(
    input: Input,
    { waitFor = "session" }: AuthenticateOptions = {},
  ): Promise<Output> {
    if (!inFlight) {
      inFlight = start(input);
    }
    const current = inFlight;

    if (waitFor === "session") {
      current.sessionAwaited = true;
      return await current.settled;
    }

    try {
      return await current.tokens;
    } catch {
      // A failed grant: wait for `onAuthenticateFailed` (a logout, a slot retry) before the caller goes on.
      return await current.settled;
    }
  }

  return { authenticate };
}
